import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  Database,
  Target,
  Settings,
  Zap,
  Shield,
  Globe,
  Layers,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  PieChart,
  LineChart,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const AnalyticsDetail = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/");
    setTimeout(() => {
      document
        .getElementById("contact")
        ?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const tableauServices = [
    {
      icon: BarChart3,
      name: "Tableau Desktop",
      description:
        "Advanced data visualization, interactive dashboards, and self-service analytics",
    },
    {
      icon: Database,
      name: "Tableau Server",
      description:
        "Enterprise deployment, collaboration, governance, and centralized analytics",
    },
    {
      icon: Globe,
      name: "Tableau Online",
      description:
        "Cloud-based analytics platform with automatic updates and scalability",
    },
    {
      icon: Zap,
      name: "Tableau Prep",
      description:
        "Data preparation, cleaning, transformation workflows, and quality management",
    },
    {
      icon: Target,
      name: "Tableau CRM",
      description:
        "Salesforce-embedded analytics with AI-powered insights and predictions",
    },
    {
      icon: Settings,
      name: "Tableau Data Management",
      description:
        "Data catalog, lineage tracking, quality monitoring, and governance",
    },
  ];

  const powerBIServices = [
    {
      icon: BarChart3,
      name: "Power BI Desktop",
      description:
        "Report creation, data modeling, visualization design, and DAX calculations",
    },
    {
      icon: Globe,
      name: "Power BI Service",
      description:
        "Cloud sharing, collaboration, scheduled refresh, and workspace management",
    },
    {
      icon: Database,
      name: "Power BI Premium",
      description:
        "Large-scale deployment, paginated reports, AI capabilities, and dedicated capacity",
    },
    {
      icon: Settings,
      name: "Power BI Embedded",
      description:
        "Application integration, white-label analytics, and customer-facing reports",
    },
    {
      icon: Zap,
      name: "Power Query",
      description:
        "Data transformation, ETL processes, connector ecosystem, and automation",
    },
    {
      icon: Shield,
      name: "Power BI Gateway",
      description:
        "On-premises data connectivity, security, and refresh management",
    },
  ];

  const kinaxisServices = [
    {
      icon: TrendingUp,
      name: "Demand Planning",
      description:
        "Statistical forecasting, demand sensing, and machine learning for accurate predictions",
    },
    {
      icon: Layers,
      name: "Supply Planning",
      description:
        "Capacity planning, constraint optimization, and multi-site production scheduling",
    },
    {
      icon: BarChart3,
      name: "S&OP Platform",
      description:
        "Integrated business planning with financial reconciliation and scenario analysis",
    },
    {
      icon: Zap,
      name: "RapidResponse",
      description:
        "Real-time supply chain visibility, what-if analysis, and concurrent planning",
    },
    {
      icon: Database,
      name: "Inventory Optimization",
      description:
        "Multi-echelon inventory planning, safety stock calculations, and demand-driven replenishment",
    },
    {
      icon: Globe,
      name: "Supply Chain Control Tower",
      description:
        "End-to-end visibility, exception management, and collaborative decision-making",
    },
  ];

  const implementationPhases = [
    {
      phase: "Data Discovery",
      duration: "2-4 weeks",
      activities: [
        "Data source identification",
        "Quality assessment",
        "Governance framework",
        "Requirements gathering",
      ],
    },
    {
      phase: "Architecture Design",
      duration: "3-6 weeks",
      activities: [
        "Data warehouse design",
        "ETL processes",
        "Security framework",
        "Performance optimization",
      ],
    },
    {
      phase: "Development & Testing",
      duration: "8-16 weeks",
      activities: [
        "Dashboard creation",
        "Report development",
        "Data modeling",
        "User acceptance testing",
      ],
    },
    {
      phase: "Deployment & Training",
      duration: "4-8 weeks",
      activities: [
        "Production deployment",
        "User training",
        "Documentation",
        "Performance monitoring",
      ],
    },
  ];

  const benefits = [
    { metric: "Decision Speed", value: "300% Faster", icon: TrendingUp },
    { metric: "Data Accuracy", value: "95% Improvement", icon: Database },
    { metric: "Reporting Time", value: "80% Reduction", icon: BarChart3 },
    { metric: "User Adoption", value: "90% Success Rate", icon: Target },
  ];

  const analyticsCapabilities = [
    {
      category: "Data Visualization",
      features: [
        "Interactive dashboards",
        "Real-time charts",
        "Custom visualizations",
        "Mobile optimization",
      ],
    },
    {
      category: "Advanced Analytics",
      features: [
        "Predictive modeling",
        "Statistical analysis",
        "Machine learning",
        "Forecasting",
      ],
    },
    {
      category: "Data Integration",
      features: [
        "Multi-source connectivity",
        "ETL processes",
        "Real-time streaming",
        "API integrations",
      ],
    },
    {
      category: "Collaboration",
      features: [
        "Shared workspaces",
        "Report distribution",
        "Comment systems",
        "Version control",
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="Analytics & BI Services - Tableau & Power BI | Smartborn"
        description="Expert Tableau and Power BI implementation services for data visualization and business intelligence. Transform your data into actionable insights with certified analytics consultants."
        keywords="Tableau implementation, Power BI services, business intelligence, data visualization, analytics consulting, dashboard development, BI solutions"
        canonical="/analytics-detail"
      />

      <div className="min-h-screen bg-background">
        <Navigation />

        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-background via-primary/3 to-secondary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <Badge className="mb-6 bg-secondary/10 text-secondary border-secondary/30">
                  Analytics Experts
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-duo-gradient bg-clip-text text-transparent">
                  Analytics & Business Intelligence
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Transform your data into actionable insights with expert
                  Tableau and Power BI implementations. Drive data-driven
                  decisions across your organization.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-gradient-primary hover:opacity-90"
                  >
                    Start Analytics Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    View All Solutions
                  </Button>
                </div>
              </div>
            </div>
          </section>

          {/* Implementation Approach */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Data-Driven Transformation Strategy
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive approach to turn your data into competitive
                  advantage
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {implementationPhases.map((phase, index) => (
                  <Card
                    key={index}
                    className="relative bg-gradient-card border-0 shadow-elegant"
                  >
                    <CardHeader className="text-center pb-4">
                      <div className="bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-glow">
                        {index + 1}
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {phase.phase}
                      </CardTitle>
                      <Badge variant="outline" className="mx-auto">
                        {phase.duration}
                      </Badge>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {phase.activities.map((activity, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-muted-foreground"
                          >
                            <CheckCircle className={`h-4 w-4 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Platform Comparison */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Leading Analytics Platforms
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Expert implementation services for both Tableau and Power BI
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Tableau Section */}
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-white">
                      <PieChart className="h-5 w-5" />
                      <span className="font-semibold">Tableau Excellence</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    {tableauServices.map((service, index) => (
                      <Card
                        key={index}
                        className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="bg-primary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300">
                              <service.icon className="h-5 w-5 text-white shadow-glow" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground mb-2 text-lg">
                                {service.name}
                              </h3>
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {service.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Power BI Section */}
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-white">
                      <LineChart className="h-5 w-5" />
                      <span className="font-semibold">Power BI Mastery</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-6">
                    {powerBIServices.map((service, index) => (
                      <Card
                        key={index}
                        className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="bg-secondary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300">
                              <service.icon className="h-5 w-5 text-white shadow-glow-secondary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground mb-2 text-lg">
                                {service.name}
                              </h3>
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {service.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Analytics Capabilities */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Advanced Analytics Capabilities
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive feature set to unlock your data's potential
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {analyticsCapabilities.map((capability, index) => (
                  <Card
                    key={index}
                    className="bg-gradient-card border-0 shadow-elegant"
                  >
                    <CardHeader className="text-center">
                      <div className="bg-gradient-primary p-3 rounded-lg inline-flex mx-auto mb-4">
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">
                        {capability.category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {capability.features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm text-muted-foreground"
                          >
                            <CheckCircle className={`h-4 w-4 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Business Intelligence ROI */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Business Intelligence ROI
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Proven impact on decision-making and business performance
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card
                    key={index}
                    className="text-center bg-gradient-card border-0 shadow-elegant"
                  >
                    <CardContent className="p-8">
                      <benefit.icon className={`h-12 w-12 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mx-auto mb-4`} />
                      <h3 className="font-semibold text-foreground mb-2 text-lg">
                        {benefit.metric}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {benefit.value}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Ready to Unlock Your Data's Potential?
                </h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Partner with certified Tableau and Power BI experts to
                  transform your data into competitive advantage and drive
                  data-driven decisions.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={handleGetStarted}
                    className="bg-gradient-primary hover:opacity-90"
                  >
                    Get Analytics Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                  >
                    Explore More Solutions
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AnalyticsDetail;
